import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Trash2 } from "lucide-react";

export default function AdminAnnouncementsTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", message: "", type: "info", target: "all" });

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/announcements`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setAnnouncements(data.announcements);
    } catch (error) { console.error("Announcements error:", error); }
  };

  const createAnnouncement = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/announcements`, { method: "POST", headers: getAuthHeaders(), credentials: "include", body: JSON.stringify(newAnnouncement) });
      if (res.ok) { toast.success("Announcement created"); setNewAnnouncement({ title: "", message: "", type: "info", target: "all" }); loadAnnouncements(); }
    } catch (error) { toast.error("Failed to create announcement"); }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const res = await credFetch(`${apiUrl}/api/admin/announcements/${id}`, { method: "DELETE", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("Announcement deleted"); loadAnnouncements(); }
    } catch (error) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-announcements-tab">
      <div className="flex justify-between items-center"><h2 className="text-2xl font-bold text-gray-900">Announcements</h2></div>
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Create New Announcement</h3>
        <div className="space-y-4">
          <div><label className="text-sm font-medium">Title</label><Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="Announcement title" /></div>
          <div><label className="text-sm font-medium">Message</label><Textarea value={newAnnouncement.message} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })} placeholder="Announcement message..." rows={3} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Type</label><Select value={newAnnouncement.type} onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, type: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="success">Success</SelectItem><SelectItem value="error">Error</SelectItem></SelectContent></Select></div>
            <div><label className="text-sm font-medium">Target Users</label><Select value={newAnnouncement.target} onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, target: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Users</SelectItem><SelectItem value="pro">Pro Users Only</SelectItem><SelectItem value="free">Free Users Only</SelectItem></SelectContent></Select></div>
          </div>
          <Button onClick={createAnnouncement} className="bg-violet-600 hover:bg-violet-700"><Megaphone className="w-4 h-4 mr-2" />Create Announcement</Button>
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Active Announcements</h3>
        {announcements.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No announcements yet</p>
        ) : (
          <div className="space-y-3">
            {announcements.map((ann) => (
              <div key={ann.id} className={`p-4 rounded-lg border-l-4 ${ann.type === "info" ? "bg-blue-50 border-blue-500" : ann.type === "warning" ? "bg-yellow-50 border-yellow-500" : ann.type === "success" ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{ann.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{ann.message}</p>
                    <div className="flex gap-2 mt-2"><Badge variant="outline">{ann.target}</Badge><span className="text-xs text-gray-500">{new Date(ann.created_at).toLocaleDateString()}</span></div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteAnnouncement(ann.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
